import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip  } from 'recharts';
import { Tag } from 'lucide-react';

// ❌ ELIMINAR ESTE IMPORT (ya no se necesita)
// import { fetchStatsProductos } from '../../api';

export default function ProductoPieChart({ 
  distribucionData = [], 
  multiProductoStats = {}, // Recibido desde dashboard consolidado
  isDarkMode = true,
  onProductoClick = null
}) {
  // ✅ ELIMINAR useState y useEffect
  // Los stats vienen directamente de las props (dashboard consolidado)
  const stats = multiProductoStats;

  const cardClasses = isDarkMode
    ? 'bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 shadow-lg'
    : 'bg-white border border-gray-200 rounded-xl p-6 shadow-lg';

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';

  // Colores para productos (paleta purple/pink/yellow)
  const COLORS = [
    '#6c37c0ff', // Resale Cloud(Morado Aclarado)
    '#04d9ff', // Microsoft 365 (Azul MS Claro)
    '#ff0080', // Microsoft Azure (Azul Azure Claro)ff0080
    '#feca57', // Amazon Web Services (Amarillo AWS Brillante)
    '#00ffb3', // Cloud Professional Services (Verde Turquesa Brillante)
    '#c88dff', // SaaS Support (Lavanda Pastel)
    '#667eea', // Google Cloud Platform (Azul Google Claro)
    '#ff0080', // Manufacturer Support (Rosa Brillante)
    '#ff6b6b', // Huawei Cloud (Rojo Claro)
    '#f093fb', // Google Workspace (Naranja Brillante)
    '#7CDED6', // Cloud Support (Turquesa Claro)
    '#FF9582', // Microsoft Software (Coral Pastel)
    '#52D9C2', // Enlaces Directos (Verde Claro)
    '#F25022', // Spanning (Cyan Claro)
    '#53bce6', // Plesk (Gris Claro)
  ];


  // Preparar datos
  const pieData = distribucionData.map((item, index) => ({
    name: item.producto,
    value: item.total_clientes,
    color: COLORS[index % COLORS.length]
  }));

  const clientesPorProducto = pieData.reduce((sum, item) => sum + item.value, 0); // 137 - suma por producto
  const totalProductos = pieData.length; // 16 - cantidad de productos
  const totalMultiProducto = stats.total_multi_producto || 0;
  const porcentajeMultiProducto = stats.porcentaje_multi_producto || 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percent = clientesPorProducto > 0 ? ((data.value / clientesPorProducto) * 100).toFixed(1) : 0;
      
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-gray-900/95 border-2 border-purple-500/50' 
            : 'bg-white/95 border-2 border-purple-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-bold mb-2 text-base`}>{data.name}</p>
          <p className={`${textSecondary} text-sm mb-1`}>
            Clientes: <span className={`${textPrimary} font-semibold`}>{data.value}</span>
          </p>
          <p className={`${textSecondary} text-sm`}>
            Porcentaje: <span className={`${textPrimary} font-semibold`}>{percent}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
          <Tag className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className={`${textPrimary} text-xl font-bold`}>
            Distribución de Clientes por Producto
          </h3>
          <p className={`${textSecondary} text-sm`}>
            {totalMultiProducto} clientes con múltiples productos ({porcentajeMultiProducto}%)
          </p>
        </div>
      </div>

      {/* Gráfica */}
      {pieData.length === 0 ? (
        <div className={`${textSecondary} text-center py-8`}>
          No hay datos disponibles
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                onClick={(data) => onProductoClick && onProductoClick(data.name)}
                style={{ cursor: onProductoClick ? 'pointer' : 'default' }}
              >
                {pieData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    className="transition-opacity hover:opacity-80"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* TARJETAS DE PRODUCTOS - Grid 4 columnas */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {pieData.map((producto, index) => (
              <div
                key={index}
                className={`${
                  isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                } rounded-lg p-2 flex items-center gap-2 cursor-pointer hover:bg-white/10 transition-all`}
                onClick={() => onProductoClick && onProductoClick(producto.name)}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: producto.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className={`${textPrimary} font-semibold text-sm truncate`}>
                    {producto.name}
                  </p>
                  <p className={`${textSecondary} text-m`}>
                    {producto.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer con estadísticas - SIN CLIENTES ÚNICOS */}
          <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Clientes por Producto</p>
                <p className={`${textPrimary} text-2xl font-bold`}>
                  {clientesPorProducto}
                </p>
                <p className={`${textSecondary} text-[10px]`}>(suma por producto)</p>
              </div>
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Multi-Producto</p>
                <p className={`${textPrimary} text-2xl font-bold text-purple-400`}>
                  {totalMultiProducto}
                </p>
                <p className={`${textSecondary} text-[10px]`}>({porcentajeMultiProducto}%)</p>
              </div>
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Total Productos</p>
                <p className={`${textPrimary} text-2xl font-bold text-pink-400`}>
                  {totalProductos}
                </p>
                <p className={`${textSecondary} text-[10px]`}>(productos activos)</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}